import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { keys } from "../../../api/queryKeys.ts";
import { api, type BackgroundJob } from "../../../api/client.ts";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import JobName from "../../../components/dashboard/platform/JobName.tsx";
import PageHeader from "../../../components/PageHeader.tsx";

function BackgroundJobsPage() {
  const queryClient = useQueryClient();

  const { data: jobs, isLoading } = useQuery({
    queryKey: keys.backgroundJob.list(),
    queryFn: api.getBackgroundJobs,
  });

  // By category, then country, then name: a country's jobs sit together.
  const sorted = [...(jobs ?? [])].sort(
    (a, b) =>
      a.category_label.localeCompare(b.category_label) ||
      (a.country ?? "~").localeCompare(b.country ?? "~") ||
      a.name.localeCompare(b.name),
  );

  const toggleMutation = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.updateBackgroundJob(key, enabled),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: keys.backgroundJob.list() });
      toast.success(`${updated.name} ${updated.enabled ? "enabled" : "disabled"}`);
    },
  });

  return (
    <>
      <PageHeader
        title="Background jobs"
        description="Enable or disable continuous background research workflows"
      />
      <TableCard loading={isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Job</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Enabled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((job: BackgroundJob) => (
              <TableRow key={job.job_key}>
                <TableCell>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 600, display: "flex" }}>
                    <JobName country={job.country}>{job.name}</JobName>
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {job.category_label}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {job.description}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Switch
                    checked={job.enabled}
                    onChange={() =>
                      toggleMutation.mutate({
                        key: job.job_key,
                        enabled: !job.enabled,
                      })
                    }
                    disabled={toggleMutation.isPending}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            {jobs?.length === 0 && <EmptyTableRow colSpan={4}>No background jobs</EmptyTableRow>}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </>
  );
}

export default BackgroundJobsPage;
